import React, { useState, useEffect } from 'react';
import { testimonials } from '../data/mockData';
import { Star, Quote, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';

const TestimonialsSection = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      if (!isAnimating) {
        setIsAnimating(true);
        setCurrentIndex((prev) => (prev + 1) % testimonials.length);
        setTimeout(() => setIsAnimating(false), 500);
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [isAnimating]);

  const handleNav = (dir) => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentIndex((prev) => dir === 'next' ? (prev + 1) % testimonials.length : (prev - 1 + testimonials.length) % testimonials.length);
    setTimeout(() => setIsAnimating(false), 500);
  };

  const getVisible = () => {
    const prev = (currentIndex - 1 + testimonials.length) % testimonials.length;
    const next = (currentIndex + 1) % testimonials.length;
    return [testimonials[prev], testimonials[currentIndex], testimonials[next]];
  };

  const visible = getVisible();

  return (
    <section id="testimonials" className="py-24 relative" style={{ background: 'linear-gradient(180deg, #1A0A0E 0%, #0D0507 100%)' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-gold-400 mb-3">Client Reviews</p>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-white mb-4">What Our Clients Say</h2>
          <p className="text-wine-300">Real feedback from businesses that trusted us with their trademark registration.</p>
        </div>

        <div className="relative max-w-5xl mx-auto">
          <div className="grid md:grid-cols-3 gap-5">
            {visible.map((t, i) => (
              <div key={`${t.id}-${i}`} data-testid="testimonial-card"
                className={`bg-wine-900/50 rounded-xl p-7 border transition-all duration-500 ${i === 1 ? 'border-gold-400/40 shadow-lg shadow-gold-500/10' : 'border-wine-800/30 opacity-60 hidden md:block'}`}>
                <Quote className="w-8 h-8 text-wine-700 mb-4" />
                <p className="text-wine-200 text-sm leading-relaxed mb-6 min-h-[80px]">"{t.content}"</p>
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(t.rating)].map((_, j) => (
                    <Star key={j} className="w-4 h-4 fill-gold-400 text-gold-400" />
                  ))}
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-wine-800 text-gold-400 flex items-center justify-center font-bold text-sm border border-wine-700/50">{t.name.charAt(0)}</div>
                  <div>
                    <div className="font-semibold text-white text-sm">{t.name}</div>
                    <div className="text-xs text-wine-400">{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="flex items-center justify-center gap-4 mt-10">
            <Button data-testid="testimonial-prev-btn" onClick={() => handleNav('prev')} disabled={isAnimating} variant="outline"
              className="rounded-full w-10 h-10 p-0 border-wine-700 bg-wine-900/50 hover:bg-wine-800 text-wine-300 hover:text-white transition-colors">
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-2">
              {testimonials.map((_, index) => (
                <button key={index} onClick={() => { if (!isAnimating) { setIsAnimating(true); setCurrentIndex(index); setTimeout(() => setIsAnimating(false), 500); } }}
                  className={`rounded-full transition-all ${index === currentIndex ? 'w-6 h-2 bg-gold-400' : 'w-2 h-2 bg-wine-700 hover:bg-wine-500'}`} />
              ))}
            </div>
            <Button data-testid="testimonial-next-btn" onClick={() => handleNav('next')} disabled={isAnimating} variant="outline"
              className="rounded-full w-10 h-10 p-0 border-wine-700 bg-wine-900/50 hover:bg-wine-800 text-wine-300 hover:text-white transition-colors">
              <ChevronRight className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
